import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, UserPlus, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";

const field = "bg-[#0f0819] border-white/15 focus-visible:ring-pink-500/40 text-white placeholder:text-white/30 h-12 rounded-lg";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const isSignup = loc.pathname === "/signup" || loc.pathname === "/register";
  const [mode, setMode] = useState(isSignup ? "register" : "login");
  const [userType, setUserType] = useState(params.get("type") || localStorage.getItem("ff_role") || "fan");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (params.get("type")) setUserType(params.get("type")); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "register") u = await register(form.name, form.email, form.password, userType);
      else u = await login(form.email, form.password);
      toast.success(`Welcome to Fan Focus`);
      if (u.type === "fan" && mode === "register") nav("/interests");
      else if (u.type === "fan") nav("/fan/dashboard");
      else nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const isCreator = userType === "creator";
  const accent = isCreator ? "bg-brand" : "bg-violet-500";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
      <div className="w-full max-w-md">
        <div className="card p-8" data-testid="auth-card">
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-2 p-1 bg-black/40 rounded-lg mb-6" data-testid="type-toggle">
              <button type="button" onClick={() => setUserType("creator")} data-testid="type-creator"
                className={`py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${userType==="creator" ? "bg-brand text-white" : "text-white/60"}`}>
                <UserPlus size={15} /> Creator
              </button>
              <button type="button" onClick={() => setUserType("fan")} data-testid="type-fan"
                className={`py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${userType==="fan" ? "bg-violet-500 text-white" : "text-white/60"}`}>
                <Heart size={15} /> Fan
              </button>
            </div>
          )}
          <h1 className="font-display text-3xl text-white mb-1">
            {mode === "register" ? (isCreator ? "Join as a Creator" : "Join Free as a Fan") : "Welcome Back"}
          </h1>
          <p className="text-white/50 text-sm mb-6">
            {mode === "register" ? (isCreator ? "Set up your creator account." : "No monthly fee. Ever.") : "Log in to your account."}
          </p>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <Input data-testid="auth-name" className={field} placeholder="Your name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required />
            )}
            <Input data-testid="auth-email" type="email" className={field} placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} required />
            <Input data-testid="auth-password" type="password" className={field} placeholder="Password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} required />
            <button type="submit" disabled={busy} data-testid="auth-submit"
              className={`w-full ${accent} text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60`}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>{mode === "register" ? "Create Account" : "Log In"} <ArrowRight size={15} /></>}
            </button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-white/30 text-xs uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button type="button" data-testid="google-signin-btn" onClick={() => {
            const redirectUrl = window.location.origin + "/login?grole=" + userType;
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
            className="w-full bg-white text-gray-800 py-3 rounded-lg font-semibold flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.58 2.68-3.9 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.95l3.01 2.33C6.68 5.16 8.66 3.58 9 3.58z"/></svg>
            Continue with Google
          </button>
          <p className="mt-5 text-white/50 text-sm text-center">
            {mode === "register" ? "Already have an account?" : "New here?"}{" "}
            <button data-testid="auth-toggle" onClick={()=>setMode(mode==="register"?"login":"register")} className="text-brand font-semibold">
              {mode === "register" ? "Log in" : "Create one"}
            </button>
          </p>
          {mode === "login" && <p className="mt-3 text-center text-white/30 text-xs">Demo creator: creator@fanf0cus.com / autopilot123</p>}
        </div>
        <p className="mt-4 text-center text-white/40 text-xs"><Link to="/" className="hover:text-white">← Back to home</Link></p>
      </div>
    </div>
  );
}
